import { useCallback, useEffect, useRef, useState } from 'react'
import type { MallSceneManager } from './mallScene'
import type { ControlMode } from './mallControlMode'

const LOOK_SENS = 0.0032       // radians per pixel, mouse/1-finger drag
const STRAFE_SENS = 0.012      // strafe input per pixel of 2-finger centroid offset
const PINCH_MOVE_SENS = 0.008  // forward/backward input per pixel of pinch spread

const MOVE_KEYS: Record<string, { forward?: number; strafe?: number }> = {
  w: { forward: 1 }, ArrowUp: { forward: 1 },
  s: { forward: -1 }, ArrowDown: { forward: -1 },
  a: { strafe: -1 }, ArrowLeft: { strafe: -1 },
  d: { strafe: 1 }, ArrowRight: { strafe: 1 },
}

function centroid(t0: Touch, t1: Touch) {
  return { x: (t0.clientX + t1.clientX) / 2, y: (t0.clientY + t1.clientY) / 2 }
}

function distance(t0: Touch, t1: Touch) {
  return Math.hypot(t0.clientX - t1.clientX, t0.clientY - t1.clientY)
}

export interface MallInputOptions {
  /** Fired by click-while-locked or the E key — "walk into the shop in front of me". */
  onInteract?: () => void
  /** 'joystick' hands movement off to <MobileJoystick>; this hook then only
   *  handles look-drag on touch. 'gesture' keeps the built-in pinch-to-move. */
  controlMode?: ControlMode
}

export interface MallInputState {
  /** True while the mouse is captured and moving it turns the view. */
  pointerLocked: boolean
  /** Hands the cursor back so HUD buttons become clickable again. */
  releasePointer: () => void
}

/**
 * Attaches desktop and mobile input to the canvas container, feeding deltas
 * into a MallSceneManager. Kept separate from mallScene.ts so rendering and
 * input concerns don't mix.
 *
 * Desktop look uses the Pointer Lock API: click once to capture the mouse, then
 * moving it turns the view with no button held, as in any first-person game.
 * Esc hands the cursor back. Where pointer lock is unavailable or refused —
 * some embedded webviews, or a second lock request too soon after the last —
 * it silently falls back to click-and-drag, so looking around never dead-ends.
 */
export function useMallInputController(
  containerRef: React.RefObject<HTMLDivElement | null>,
  sceneRef: React.RefObject<MallSceneManager | null>,
  options: MallInputOptions = {}
): MallInputState {
  const heldKeys = useRef<Set<string>>(new Set())
  const [pointerLocked, setPointerLocked] = useState(false)

  // Read through refs so the listener effect doesn't re-run every render
  const onInteractRef = useRef(options.onInteract)
  onInteractRef.current = options.onInteract
  const controlModeRef = useRef<ControlMode>(options.controlMode ?? 'joystick')
  controlModeRef.current = options.controlMode ?? 'joystick'

  const releasePointer = useCallback(() => {
    if (document.pointerLockElement) document.exitPointerLock()
  }, [])

  // Recompute forward/strafe from currently-held keys (desktop, discrete on/off)
  const applyHeldKeys = () => {
    let forward = 0
    let strafe = 0
    heldKeys.current.forEach((key) => {
      const m = MOVE_KEYS[key]
      if (!m) return
      if (m.forward) forward += m.forward
      if (m.strafe) strafe += m.strafe
    })
    sceneRef.current?.setMoveInput(forward, strafe)
  }

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const isLocked = () => document.pointerLockElement === container

    // ── Desktop: keyboard movement + interact ───────────────────────
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null
      if (target && ['INPUT', 'TEXTAREA'].includes(target.tagName)) return

      if (e.key === 'e' || e.key === 'E') {
        onInteractRef.current?.()
        return
      }
      if (!MOVE_KEYS[e.key]) return
      e.preventDefault()
      heldKeys.current.add(e.key)
      applyHeldKeys()
    }
    const onKeyUp = (e: KeyboardEvent) => {
      if (!MOVE_KEYS[e.key]) return
      heldKeys.current.delete(e.key)
      applyHeldKeys()
    }
    const onBlur = () => {
      heldKeys.current.clear()
      applyHeldKeys()
    }
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)
    window.addEventListener('blur', onBlur)

    // ── Desktop: pointer-lock mouse-look, with drag as the fallback ──
    let dragging = false
    let lastX = 0
    let lastY = 0

    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return

      if (isLocked()) {
        // Already looking around: a click is the interact button.
        onInteractRef.current?.()
        return
      }

      // requestPointerLock rejects if called too soon after an exit, or if the
      // document isn't allowed to capture. Fall back to dragging rather than
      // leaving the user unable to look.
      const request = container.requestPointerLock() as unknown as Promise<void> | undefined
      if (request && typeof request.catch === 'function') {
        request.catch(() => { /* fallback below already armed */ })
      }
      dragging = true
      lastX = e.clientX
      lastY = e.clientY
      try { container.setPointerCapture(e.pointerId) } catch { /* not capturable */ }
    }

    const onPointerMove = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return
      if (isLocked()) {
        sceneRef.current?.applyLookDelta(e.movementX * LOOK_SENS, e.movementY * LOOK_SENS)
        return
      }
      if (!dragging) return
      const dx = e.clientX - lastX
      const dy = e.clientY - lastY
      lastX = e.clientX
      lastY = e.clientY
      sceneRef.current?.applyLookDelta(dx * LOOK_SENS, dy * LOOK_SENS)
    }

    const onPointerUp = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return
      dragging = false
      try { container.releasePointerCapture(e.pointerId) } catch { /* already released */ }
    }

    const onPointerLockChange = () => {
      const locked = isLocked()
      setPointerLocked(locked)
      // Capturing the mouse supersedes the drag fallback for this gesture.
      if (locked) dragging = false
    }
    const onPointerLockError = () => setPointerLocked(false)

    container.addEventListener('pointerdown', onPointerDown)
    container.addEventListener('pointermove', onPointerMove)
    container.addEventListener('pointerup', onPointerUp)
    container.addEventListener('pointerleave', onPointerUp)
    document.addEventListener('pointerlockchange', onPointerLockChange)
    document.addEventListener('pointerlockerror', onPointerLockError)

    // ── Mobile: look-drag, plus (gesture mode only) pinch-to-move ────
    // In 'joystick' mode, <MobileJoystick> owns movement entirely on its own
    // DOM node — touches over it never reach this container — so here we only
    // ever need to track a single look touch by identifier. That identifier
    // tracking also protects against e.touches including the joystick's touch
    // (touches lists every active touch on the screen, not just this
    // element's), which plain touches.length counting would misread as a
    // second finger down on the canvas.
    let touchMode: 'none' | 'look' | 'move' = 'none'
    let lastTouch = { x: 0, y: 0 }
    let lookTouchId: number | null = null
    // Pinch anchors (gesture mode only). Movement is held-based off these
    // (like a joystick), not incremental drag deltas — spread the fingers apart
    // and hold to keep walking forward, the same gesture as pinch-zoom, rather
    // than requiring a continuous drag.
    let pinchStartDistance = 0
    let pinchStartCentroid = { x: 0, y: 0 }

    const onTouchStart = (e: TouchEvent) => {
      if (controlModeRef.current === 'joystick') {
        if (lookTouchId === null) {
          const t = e.changedTouches[0]
          lookTouchId = t.identifier
          lastTouch = { x: t.clientX, y: t.clientY }
        }
        return
      }
      if (e.touches.length === 1) {
        touchMode = 'look'
        lastTouch = { x: e.touches[0].clientX, y: e.touches[0].clientY }
      } else if (e.touches.length === 2) {
        touchMode = 'move'
        pinchStartDistance = distance(e.touches[0], e.touches[1])
        pinchStartCentroid = centroid(e.touches[0], e.touches[1])
      }
    }
    const onTouchMove = (e: TouchEvent) => {
      if (controlModeRef.current === 'joystick') {
        const t = Array.from(e.touches).find((c) => c.identifier === lookTouchId)
        if (!t) return
        e.preventDefault()
        const dx = t.clientX - lastTouch.x
        const dy = t.clientY - lastTouch.y
        lastTouch = { x: t.clientX, y: t.clientY }
        sceneRef.current?.applyLookDelta(dx * LOOK_SENS, dy * LOOK_SENS)
        return
      }
      e.preventDefault()
      if (e.touches.length === 1 && touchMode === 'look') {
        const t = e.touches[0]
        const dx = t.clientX - lastTouch.x
        const dy = t.clientY - lastTouch.y
        lastTouch = { x: t.clientX, y: t.clientY }
        sceneRef.current?.applyLookDelta(dx * LOOK_SENS, dy * LOOK_SENS)
      } else if (e.touches.length === 2 && touchMode === 'move') {
        // Spreading the fingers apart (distance grows) = walk forward;
        // pinching them together (distance shrinks) = walk backward.
        // Shifting the whole pinch sideways still strafes.
        const d = distance(e.touches[0], e.touches[1])
        const c = centroid(e.touches[0], e.touches[1])
        const forward = clamp((d - pinchStartDistance) * PINCH_MOVE_SENS, -1, 1)
        const strafe = clamp((c.x - pinchStartCentroid.x) * STRAFE_SENS, -1, 1)
        sceneRef.current?.setMoveInput(forward, strafe)
      }
    }
    const onTouchEnd = (e: TouchEvent) => {
      if (controlModeRef.current === 'joystick') {
        const ended = Array.from(e.changedTouches).some((c) => c.identifier === lookTouchId)
        if (ended) lookTouchId = null
        return
      }
      if (e.touches.length === 0) {
        touchMode = 'none'
        sceneRef.current?.setMoveInput(0, 0)
      } else if (e.touches.length === 1) {
        // 2→1 finger mid-gesture: reset anchor so no stale-delta jump.
        touchMode = 'look'
        lastTouch = { x: e.touches[0].clientX, y: e.touches[0].clientY }
        sceneRef.current?.setMoveInput(0, 0)
      }
    }
    const onTouchCancel = () => {
      lookTouchId = null
      touchMode = 'none'
      sceneRef.current?.setMoveInput(0, 0)
    }

    container.addEventListener('touchstart', onTouchStart, { passive: true })
    container.addEventListener('touchmove', onTouchMove, { passive: false })
    container.addEventListener('touchend', onTouchEnd, { passive: true })
    container.addEventListener('touchcancel', onTouchCancel, { passive: true })

    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
      window.removeEventListener('blur', onBlur)
      container.removeEventListener('pointerdown', onPointerDown)
      container.removeEventListener('pointermove', onPointerMove)
      container.removeEventListener('pointerup', onPointerUp)
      container.removeEventListener('pointerleave', onPointerUp)
      document.removeEventListener('pointerlockchange', onPointerLockChange)
      document.removeEventListener('pointerlockerror', onPointerLockError)
      container.removeEventListener('touchstart', onTouchStart)
      container.removeEventListener('touchmove', onTouchMove)
      container.removeEventListener('touchend', onTouchEnd)
      container.removeEventListener('touchcancel', onTouchCancel)
      if (document.pointerLockElement === container) document.exitPointerLock()
    }
  }, [containerRef, sceneRef])

  return { pointerLocked, releasePointer }
}

function clamp(v: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, v))
}
